import { useState, useMemo } from "react";

import { View, Text, Dialog, SwitchField, FileField, FieldLabel, DropdownField, Button } from "../base";
import { InputField } from "../base";
import { Icon } from "../base/Icon";
import { useAppDispatch } from "../../redux";
import { organizationSnippetActions, OrganizationSnippet } from "../../redux/reducers/organizationSnippet";
import { makeRequest } from "../../utils/request";

interface Voice {
  label: string;
  provider: string;
  voiceId: string;
}

const VOICES: Voice[] = [
  { label: "Alloy (OpenAI)", provider: "openai", voiceId: "alloy" },
  { label: "Ash (OpenAI)", provider: "openai", voiceId: "ash" },
  { label: "Coral (OpenAI)", provider: "openai", voiceId: "coral" },
  { label: "Echo (OpenAI)", provider: "openai", voiceId: "echo" },
  { label: "Fable (OpenAI)", provider: "openai", voiceId: "fable" },
  { label: "Nova (OpenAI)", provider: "openai", voiceId: "nova" },
  { label: "Onyx (OpenAI)", provider: "openai", voiceId: "onyx" },
  { label: "Sage (OpenAI)", provider: "openai", voiceId: "sage" },
  { label: "Shimmer (OpenAI)", provider: "openai", voiceId: "shimmer" },
  { label: "Barbershop Man (Cartesia)", provider: "cartesia", voiceId: "a0e99841-438c-4a64-b679-ae501e7d6091" },
  { label: "Jenny (Azure)", provider: "azure", voiceId: "en-US-JennyNeural" },
  { label: "Wavenet D (Google)", provider: "google", voiceId: "en-US-Wavenet-D" },
  { label: "Rachel (ElevenLabs)", provider: "elevenlabs", voiceId: "21m00Tcm4TlvDq8ikWAM" },
];

type OrganizationSnippetCreateDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onCreated: (snippet: OrganizationSnippet) => void;
};

export const OrganizationSnippetCreateDialog = ({ isOpen, onClose, organizationId, onCreated }: OrganizationSnippetCreateDialogProps) => {
  const dispatch = useAppDispatch();
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | undefined>(undefined);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState<"upload" | "tts">("upload");
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const [previewAudioBlob, setPreviewAudioBlob] = useState<Blob | null>(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const selectedVoice = useMemo(() => VOICES.find((v) => v.voiceId === selectedVoiceId), [selectedVoiceId]);

  const onClickGeneratePreview = async () => {
    if (!selectedVoice || !text.trim() || mode !== "tts") return;

    setGeneratingPreview(true);

    try {
      const response = await makeRequest<Blob>("/v1/tts/provider", {
        method: "POST",
        body: {
          text: text.trim(),
          provider: selectedVoice.provider,
          voiceId: selectedVoice.voiceId,
        },
        blob: true,
      });

      if (previewAudioUrl) {
        URL.revokeObjectURL(previewAudioUrl);
      }

      setPreviewAudioBlob(response);
      setPreviewAudioUrl(URL.createObjectURL(response));
    } catch (error) {
      console.error("Failed to generate preview:", error);
      setError("Failed to generate preview. Please try again.");
    } finally {
      setGeneratingPreview(false);
    }
  };

  const onClickCreate = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    if (mode === "upload") {
      if (!uploadFile) {
        setError("File is required");
        return;
      }

      // Validate file type
      const isAudioFile = uploadFile.type.startsWith("audio/");
      const isTextFile = uploadFile.type.startsWith("text/") || uploadFile.name.toLowerCase().endsWith(".txt");

      if (!isAudioFile && !isTextFile) {
        setError("Please select a valid audio file (MP3, WAV, OGG) or text file (TXT)");
        return;
      }

      // Validate file size (e.g., 10MB limit)
      if (uploadFile.size > 10 * 1024 * 1024) {
        setError("File size must be less than 10MB");
        return;
      }
    } else {
      if (!text.trim()) {
        setError("Content is required");
        return;
      }

      if (mode === "tts" && !selectedVoiceId) {
        setError("Please select a voice");
        return;
      }

      if (mode === "tts" && text.length > 200) {
        setError("Text must be 200 characters or less for text-to-speech");
        return;
      }
    }

    setIsCreating(true);
    setError("");

    try {
      let fileUrl = undefined;

      if (mode === "upload") {
        const uploadResult = await makeRequest<{ url: string }>(`/v1/organization-files`, {
          method: "POST",
          file: uploadFile!,
        });

        fileUrl = uploadResult?.url;
      } else if (mode === "tts") {
        // Generate audio from TTS endpoint
        if (!selectedVoice) {
          setError("Please select a voice");
          return;
        }

        let audioBlob = null;

        if (previewAudioBlob) {
          audioBlob = previewAudioBlob;
        } else {
          audioBlob = await makeRequest<Blob>("/v1/tts/provider", {
            method: "POST",
            body: {
              text: text.trim(),
              provider: selectedVoice.provider,
              voiceId: selectedVoice.voiceId,
            },
            blob: true,
          });
        }

        // Convert blob to File for upload
        const audioFile = new File([audioBlob], `${name.trim().replace(/\s+/g, "-").toLowerCase()}.wav`, {
          type: audioBlob.type || "audio/wav",
        });

        // Upload the generated audio file
        const uploadResult = await makeRequest<{ url: string }>(`/v1/organization-files`, {
          method: "POST",
          file: audioFile,
        });

        fileUrl = uploadResult?.url;
      } else {
        // Text mode - create snippet directly with text data
        const createData = {
          name: name.trim(),
          type: mode,
          data: text.trim(),
        };

        const snippet = await dispatch(organizationSnippetActions.create(createData)).unwrap();
        onCreated(snippet);
        onClickClose();
        return;
      }

      // Create snippet with uploaded file URL (for both upload and TTS modes)
      const snippet = await dispatch(
        organizationSnippetActions.create({
          name: name.trim(),
          type: "audio",
          data: fileUrl,
        }),
      ).unwrap();

      onCreated(snippet);
      onClickClose();
    } catch (err) {
      setError(`Failed to ${mode === "upload" ? "upload file" : mode === "tts" ? "generate audio" : "create text"} snippet. Please try again.`);
    } finally {
      setIsCreating(false);
    }
  };

  const onClickClose = () => {
    setName("");
    setText("");
    setSelectedVoiceId(undefined);
    setError("");
    setMode("upload");
    setUploadFile(null);

    if (previewAudioUrl) {
      URL.revokeObjectURL(previewAudioUrl);
      setPreviewAudioUrl(null);
      setPreviewAudioBlob(null);
    }

    onClose();
  };

  // Convert voices to dropdown options
  const voiceOptions = VOICES.map((voice) => ({
    label: voice.label,
    value: voice.voiceId,
  }));

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClickClose}
      title="Create Snippet"
      size="lg"
      className="max-w-6xl w-[90vw]"
    >
      <View className="p-6 space-y-4">
        <SwitchField
          options={[
            { label: "Upload File", value: "upload" },
            { label: "Text-to-Speech", value: "tts" },
          ]}
          value={mode}
          onChange={(value) => setMode(value as "upload" | "tts")}
          className="w-full mb-1"
        />
        <Text className="text-sm text-gray-600 dark:text-gray-400">
          {mode === "upload"
            ? "Upload an audio file (MP3, WAV, OGG)."
            : mode === "tts"
              ? "Generate an audio snippet from text using voices from our library."
              : "Create a text snippet that can be used in your organization."}
        </Text>
        <InputField
          label="Name"
          placeholder="Enter a descriptive name"
          value={name}
          onChange={setName}
        />
        {mode === "upload" ? (
          <View className="space-y-4">
            <FieldLabel label="File">
              <FileField
                fileTypes={["audio/*"]}
                onChange={(files) => setUploadFile(files?.[0] || null)}
              />
            </FieldLabel>
            {uploadFile && (
              <View className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <View className="flex items-center gap-2">
                  <Icon
                    name="file"
                    size={20}
                    className="text-gray-500"
                  />
                  <Text className="text-sm text-gray-700 dark:text-gray-300">{uploadFile.name}</Text>
                  <Text className="text-xs text-gray-500 dark:text-gray-400">({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)</Text>
                </View>
              </View>
            )}
          </View>
        ) : (
          mode === "tts" && (
            <View className="space-y-4">
              <DropdownField
                label="Voice"
                placeholder="Select a voice"
                value={selectedVoiceId}
                onChange={(value) => setSelectedVoiceId(value)}
                options={voiceOptions}
                className="w-full"
              />
              <View className="space-y-1">
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Text to Speech</Text>
                <textarea
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
                  placeholder="Enter the text you want to convert to speech..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={200}
                />
                <Text className="text-xs text-gray-500 dark:text-gray-400">{text.length}/200 characters</Text>
              </View>
              {selectedVoice && text.trim() && (
                <View className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <View className="flex items-center justify-between">
                    <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Preview</Text>
                    <Button
                      variant="info"
                      size="auto"
                      onClick={onClickGeneratePreview}
                      disabled={generatingPreview || !text.trim()}
                    >
                      {generatingPreview ? (
                        <>
                          <Icon
                            name="loader"
                            size={14}
                            className="animate-spin"
                          />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Icon
                            name="play"
                            size={14}
                          />
                          Generate
                        </>
                      )}
                    </Button>
                  </View>

                  {previewAudioUrl && (
                    <audio
                      controls
                      src={previewAudioUrl}
                      className="w-full"
                      autoPlay
                    >
                      Your browser does not support the audio element.
                    </audio>
                  )}
                </View>
              )}
            </View>
          )
        )}
        {error && (
          <View className="mt-0.5">
            <Text className="text-sm text-red-600 dark:text-red-400">{error}</Text>
          </View>
        )}
      </View>

      <View className="flex items-center justify-end p-6 gap-3 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="neutral"
          size="auto"
          onClick={onClickClose}
          disabled={isCreating}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          size="auto"
          onClick={onClickCreate}
          disabled={isCreating || !name.trim() || (mode === "upload" && !uploadFile) || (mode === "tts" && (!text.trim() || !selectedVoiceId))}
        >
          {isCreating ? (
            <Icon
              name="loader"
              size={14}
              className="animate-spin"
            />
          ) : (
            <Icon
              name="add"
              size={14}
            />
          )}
          {mode === "upload" ? "Upload" : mode === "tts" ? "Generate" : "Create"}
        </Button>
      </View>
    </Dialog>
  );
};
